import React, { useState, useEffect } from 'react';
import { XMarkIcon, CheckCircleIcon, ExclamationTriangleIcon, UserIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import walletService from '../../services/walletService';
import useWalletStore from '../../store/useWalletStore';
import { formatNumber } from '../../utils/formatters';
import LoadingSpinner from '../common/LoadingSpinner';

const TransferFundsModal = ({ isOpen, onClose, selectedCurrency = 'BTC1' }) => {
  const [formData, setFormData] = useState({
    recipientEmail: '',
    currency: selectedCurrency,
    amount: '',
    note: ''
  });
  
  const [validationState, setValidationState] = useState({
    recipientValid: false,
    recipientInfo: null,
    emailError: '',
    amountError: '',
    isValidating: false
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState(1); // 1: Form, 2: Confirmation, 3: Success
  
  const { balances, fetchBalances } = useWalletStore();
  
  // Available currencies for transfer (only show currencies with balance > 0)
  const availableCurrencies = Object.keys(balances || {}).filter(currency => {
    const balance = balances[currency];
    return balance && balance.available > 0;
  });
  
  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      // Determine initial currency - use selectedCurrency if available, otherwise use first available currency
      const currenciesWithBalance = Object.keys(balances || {}).filter(currency => {
        const balance = balances[currency];
        return balance && balance.available > 0;
      });
      
      const initialCurrency = (balances[selectedCurrency]?.available > 0) 
        ? selectedCurrency 
        : currenciesWithBalance[0] || selectedCurrency;
      
      
      setFormData({
        recipientEmail: '',
        currency: initialCurrency,
        amount: '',
        note: ''
      });
      setValidationState({
        recipientValid: false,
        recipientInfo: null,
        emailError: '',
        amountError: '',
        isValidating: false
      });
      setStep(1);
    }
  }, [isOpen, selectedCurrency]);
  
  // Validate recipient email with debouncing
  useEffect(() => {
    const validateRecipient = async () => {
      if (!formData.recipientEmail || formData.recipientEmail.length < 3) {
        setValidationState(prev => ({
          ...prev,
          recipientValid: false,
          recipientInfo: null,
          emailError: ''
        }));
        return;
      }
      
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.recipientEmail)) {
        setValidationState(prev => ({
          ...prev,
          recipientValid: false,
          recipientInfo: null,
          emailError: 'Please enter a valid email address'
        }));
        return;
      }
      
      setValidationState(prev => ({ ...prev, isValidating: true, emailError: '' }));
      
      try {
        const response = await walletService.validateRecipient(formData.recipientEmail);
        setValidationState(prev => ({
          ...prev,
          recipientValid: true,
          recipientInfo: response.recipient,
          emailError: '',
          isValidating: false
        }));
      } catch (error) {
        setValidationState(prev => ({
          ...prev,
          recipientValid: false,
          recipientInfo: null,
          emailError: error.error || 'Recipient not found',
          isValidating: false
        }));
      }
    };
    
    const timeoutId = setTimeout(validateRecipient, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.recipientEmail]);
  
  // Validate amount
  useEffect(() => {
    if (!formData.amount) {
      setValidationState(prev => ({ ...prev, amountError: '' }));
      return;
    }
    
    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      setValidationState(prev => ({ ...prev, amountError: 'Please enter a valid amount' }));
      return;
    }
    
    // Get the current balance for the selected currency
    const selectedCurrencyBalance = balances[formData.currency]?.available || 0;
    
    
    if (amount > selectedCurrencyBalance) {
      setValidationState(prev => ({ 
        ...prev, 
        amountError: `Insufficient balance. Available: ${formatNumber(selectedCurrencyBalance, 8)} ${formData.currency}` 
      }));
      return;
    }
    
    setValidationState(prev => ({ ...prev, amountError: '' }));
  }, [formData.amount, formData.currency, balances]);
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (step === 1) {
      // Validate form before proceeding to confirmation
      if (!validationState.recipientValid || validationState.amountError || !formData.amount) {
        toast.error('Please fix all errors before proceeding');
        return;
      }
      setStep(2);
      return;
    }
    
    if (step === 2) {
      // Execute transfer
      setIsSubmitting(true);
      
      try {
        const transferData = {
          recipientEmail: formData.recipientEmail,
          currency: formData.currency,
          amount: parseFloat(formData.amount),
          note: formData.note
        };
        
        await walletService.createInternalTransfer(transferData);
        
        // Refresh balances
        await fetchBalances();
        
        setStep(3);
        toast.success('Transfer completed successfully!');
        
      } catch (error) {
        console.error('Transfer error:', error);
        toast.error(error.error || 'Transfer failed. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };
  
  const handleClose = () => {
    if (isSubmitting) return; // Prevent closing during transfer
    onClose();
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 mt-20">
      <div className="bg-gradient-to-br from-slate-800 via-slate-700 to-slate-800 rounded-2xl border-2 border-slate-600/50 shadow-2xl backdrop-blur-md w-full max-w-lg mx-4 max-h-[80vh] overflow-y-auto">
        
        {/* Header */}
        <div className="relative px-6 py-2 border-b border-slate-600/50 bg-[#0E1696]">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-800 rounded-xl flex items-center justify-center shadow-lg">
                <CurrencyDollarIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold !text-white">Transfer Funds</h3>
                <p className="text-sm !text-white">Send crypto to another user</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="!text-white hover:text-white transition-colors p-2 rounded-lg hover:bg-slate-700/50 disabled:opacity-50"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6">
          
          {/* Step 1: Transfer Form */}
          {step === 1 && (
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Recipient Email */}
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Recipient Email Address
                </label>
                <div className="relative">
                  <input
                    type="email"
                    value={formData.recipientEmail}
                    onChange={(e) => handleInputChange('recipientEmail', e.target.value)}
                    placeholder="Enter recipient's email address"
                    className={`w-full pl-4 pr-12 py-3 bg-slate-700/50 border-2 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 ${
                      validationState.emailError 
                        ? 'border-red-500/50' 
                        : validationState.recipientValid 
                        ? 'border-emerald-500/50' 
                        : 'border-slate-600/50'
                    }`}
                    required
                  />
                  
                  {/* Validation Icons */}
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    {validationState.isValidating && (
                      <LoadingSpinner size="sm" />
                    )}
                    {!validationState.isValidating && validationState.recipientValid && (
                      <CheckCircleIcon className="h-5 w-5 text-emerald-400" />
                    )}
                    {!validationState.isValidating && validationState.emailError && (
                      <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                    )}
                  </div>
                </div>
                
                {/* Recipient Info or Error */}
                {validationState.emailError && (
                  <p className="mt-2 text-sm text-red-400">{validationState.emailError}</p>
                )}
                {validationState.recipientInfo && (
                  <div className="mt-3 p-3 bg-emerald-900/30 border border-emerald-500/30 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <UserIcon className="h-5 w-5 text-emerald-400" />
                      <div>
                        <p className="text-sm font-semibold text-emerald-300">
                          {validationState.recipientInfo.first_name} {validationState.recipientInfo.last_name}
                        </p>
                        <p className="text-xs text-emerald-400">@{validationState.recipientInfo.username}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Currency Selection */}
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Currency
                </label>
                <select
                  value={formData.currency}
                  onChange={(e) => handleInputChange('currency', e.target.value)}
                  className="w-full px-4 py-3 bg-slate-700/50 border-2 border-slate-600/50 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                  required
                >
                  {availableCurrencies.map(currency => (
                    <option key={currency} value={currency}>
                      {currency} - Available: {formatNumber(balances[currency]?.available || 0, 8)}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Amount */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold text-white">
                    Amount
                  </label>
                  <span className="text-sm text-slate-400">
                    Available: {formatNumber(balances[formData.currency]?.available || 0, 8)} {formData.currency}
                  </span>
                </div>
                <div className="relative">
                  <input
                    type="number"
                    step="any"
                    value={formData.amount}
                    onChange={(e) => handleInputChange('amount', e.target.value)}
                    placeholder="0.00"
                    className={`w-full px-4 py-3 bg-slate-700/50 border-2 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 ${
                      validationState.amountError ? 'border-red-500/50' : 'border-slate-600/50'
                    }`}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => handleInputChange('amount', (balances[formData.currency]?.available || 0).toString())}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs  hover:bg-emerald-700 text-white px-2 py-1 rounded transition-colors"
                  >
                    MAX
                  </button>
                </div>
                {validationState.amountError && (
                  <p className="mt-2 text-sm text-red-400">{validationState.amountError}</p>
                )}
              </div>
              
              {/* Note (Optional) */}
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Note (Optional)
                </label>
                <textarea
                  value={formData.note}
                  onChange={(e) => handleInputChange('note', e.target.value)}
                  placeholder="Add a note to this transfer..."
                  rows={3}
                  className="w-full px-4 py-3 bg-slate-700/50 border-2 border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 resize-none"
                  maxLength={200}
                />
                <p className="mt-1 text-xs text-slate-400">{formData.note.length}/200 characters</p>
              </div>
              
              {/* Submit Button */}
              <button
                type="submit"
                disabled={!validationState.recipientValid || validationState.amountError || !formData.amount}
                className="w-full py-4 bg-[#0E1696] !text-white font-bold rounded-xl  disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-emerald-500/30"
              >
                Review Transfer
              </button>
            </form>
          )}
          
          {/* Step 2: Confirmation */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="text-center">
                <h4 className="text-lg font-bold text-white mb-2">Confirm Transfer</h4>
                <p className="text-slate-400">Please review the details before confirming</p>
              </div>
              
              {/* Transfer Summary */}
              <div className="bg-slate-700/30 rounded-xl p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-400">Recipient:</span>
                  <div className="text-right">
                    <p className="text-white font-semibold">{validationState.recipientInfo?.first_name} {validationState.recipientInfo?.last_name}</p>
                    <p className="text-sm text-slate-400">{formData.recipientEmail}</p>
                  </div>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-slate-400">Amount:</span>
                  <span className="text-white font-semibold">{formData.amount} {formData.currency}</span>
                </div>
                
                {formData.note && (
                  <div className="flex justify-between">
                    <span className="text-slate-400">Note:</span>
                    <span className="text-white max-w-48 text-right">{formData.note}</span>
                  </div>
                )}
              </div>
              
              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  disabled={isSubmitting}
                  className="flex-1 py-3 bg-slate-600 text-white font-semibold rounded-xl hover:bg-slate-700 disabled:opacity-50 transition-colors"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="flex-1 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white font-bold rounded-xl hover:from-emerald-700 hover:to-green-700 disabled:opacity-50 transition-all duration-300 flex items-center justify-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <LoadingSpinner size="sm" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <span>Confirm Transfer</span>
                  )}
                </button>
              </div>
            </div>
          )}
          
          {/* Step 3: Success */}
          {step === 3 && (
            <div className="text-center space-y-6">
              <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mx-auto">
                <CheckCircleIcon className="h-10 w-10 text-white" />
              </div>
              
              <div>
                <h4 className="text-xl font-bold text-white mb-2">Transfer Successful!</h4>
                <p className="text-slate-400">Your transfer has been completed successfully.</p>
              </div>
              
              <div className="bg-slate-700/30 rounded-xl p-4">
                <p className="text-sm text-slate-400 mb-1">Transferred</p>
                <p className="text-lg font-bold text-emerald-400">{formData.amount} {formData.currency}</p>
                <p className="text-sm text-slate-400 mt-1">to {formData.recipientEmail}</p>
              </div>
              
              <button
                onClick={handleClose}
                className="w-full py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white font-bold rounded-xl hover:from-emerald-700 hover:to-green-700 transition-all duration-300"
              >
                Done
              </button>
            </div>
          )}
          
        </div>
      </div>
    </div>
  );
};

export default TransferFundsModal;
